/**
 * Verifying the account's email (email verification & two-factor PRD): the
 * emailed link the website's `/verify-email` page reads, and what a refusal
 * means. The link carries the four parts of a signed API call. An unverified
 * account is refused almost everything else, so both platforms ask it to
 * verify, and it can always ask for a new link.
 *
 * Data-only, so React Native can read it. Import it by subpath
 * (`shared/src/config/email-verification.config`) on mobile.
 */

import { socialSignInNotice } from './social-sign-in.config';
import { API_ERROR_CODES } from '../types/api/errors';
import { emailVerificationLinkSchema } from '../validation/auth.schemas';

import type { SocialSignInNotice } from './social-sign-in.config';
import type { EmailVerificationLink } from '../types/auth';

/** How long a verification link works, as the API's mail says. */
export const VERIFICATION_LINK_MINUTES = 60;

/** The one action when a link won't do, on the page and the gates. */
export const SEND_NEW_LINK = 'Send a new link';

/** On the button while a new link is asked for. */
export const SENDING_LINK = 'Sending…';

/** Asking again, once a link was sent. */
export const RESEND_LINK = 'Resend link';

/** The pause after a new link is sent, before another can be asked for. */
export const RESEND_LINK_PAUSE_SECONDS = 60;

/** Said once a new link was sent. */
export const LINK_ON_ITS_WAY = 'Link on its way.';

/** Around the address a new link went to, which each platform sets apart. */
export const LINK_SENT_LEDE = {
  before: 'We sent a new link to ',
  after: '. It can take a minute to arrive.',
};

/** Said in the address's place when the session has none to show. */
export const EMAIL_FALLBACK = 'your email';

/** What a new link's card leads with. */
export const LINK_SENT_EYEBROW = 'Check your inbox';

/** A line of what a card says of an emailed link once it's on its way. */
export interface LinkReceiptRow {
  label: string;
  value: string;
  /** Figures that read in the mono face, such as the time. */
  mono?: boolean;
}

/**
 * What a new verification link's card says of it: how long it works, when it
 * went (a display time, e.g. "14:05" or "2:05 pm"), and where else to look.
 */
export function verificationLinkReceipt(sentAt: string): LinkReceiptRow[] {
  return [
    { label: 'Link works for', value: `${VERIFICATION_LINK_MINUTES} minutes` },
    { label: 'Sent at', value: sentAt, mono: true },
    { label: 'Not there?', value: 'Check spam or promotions' },
  ];
}

/**
 * What the unverified gate says (D3), on the website and in the apps: the
 * ask, the check for a link opened elsewhere, and signing out, for whoever
 * the account isn't.
 */
export const EMAIL_VERIFICATION_GATE = {
  eyebrow: 'One last step',
  title: 'Verify your email.',
  /** Around the address, which each platform sets apart. */
  lede: {
    before: 'Open the link we sent to ',
    after:
      ' to start using Dō. Links work for an hour, so if yours has lapsed, send a new one.',
  },
  /** Said before Check again. */
  checkLead: 'Verified it already?',
  checkAgain: 'Check again',
  checking: 'Checking…',
  /** Said before Sign out. */
  signOutLead: 'Not you?',
  signOut: 'Sign out',
} as const;

/** What `/verify-email` says, on the website and in the apps. */
export const VERIFY_EMAIL_PAGE = {
  /** Over every answer but a new link on its way. */
  eyebrow: 'Email verification',
  checking: {
    title: 'Checking your link…',
    lede: 'This only takes a moment.',
  },
  /** `after` follows the verified address, which each platform sets apart. */
  verified: {
    title: 'Email verified.',
    after: ' is verified, and all of Dō is open to you.',
  },
  alreadyVerified: {
    title: 'Already verified.',
    after: ' was verified already, so there’s nothing left to do.',
  },
  /** Said in the address's place, starting the lede, with none to show. */
  addressFallback: 'Your email',
  signedOut: {
    title: 'Log in to finish.',
    lede: 'This link verifies the email of a Dō account. Log in to it, and we’ll bring you straight back here to finish.',
  },
  unusable: {
    title: 'This link can’t be used.',
    /** Refused: the API had a session to refuse it with. */
    lede: 'Verification links work for an hour, only for the account they were sent to, and stop when its email changes. Send yourself a new one.',
    /** Never sent: a part is missing. */
    incomplete: 'This link is missing a part. Send yourself a new one.',
    incompleteSignedOut:
      'This link is missing a part. Log in, and you can send yourself a new one.',
  },
  rateLimited: {
    title: 'Too many tries.',
    lede: 'Wait a minute, then try again. Your link still works.',
  },
  unreachable: {
    title: 'Couldn’t reach Dō.',
    lede: 'Check your connection and try again. Your link still works.',
  },
  logIn: 'Log in',
  tryAgain: 'Try again',
  goToDo: 'Go to Dō',
  /** The website's, on a phone, where it can't finish: the link to the app (D12). */
  openInApp: {
    lead: 'Have the Dō app?',
    label: 'Open this link in it',
  },
} as const;

/** A request that never reached the API: the link, or the ask, still works. */
export const COULD_NOT_REACH_DO = {
  title: 'Couldn’t reach Dō',
  body: 'Check your connection and try again.',
};

/**
 * Read an emailed link's four parts from the page's query. Undefined when a
 * part is missing or empty: such a link can't verify anything, so the page
 * says so without calling. Anything else in the query is left behind, since
 * the call's signature allows nothing else.
 */
export function verificationLinkFrom(
  params: URLSearchParams
): EmailVerificationLink | undefined {
  const parsed = emailVerificationLinkSchema.safeParse({
    id: params.get('id'),
    hash: params.get('hash'),
    expires: params.get('expires'),
    signature: params.get('signature'),
  });

  return parsed.success ? parsed.data : undefined;
}

/**
 * The link's four parts as a query, for the apps' way back to their verify
 * screen and the website's hand-off to the app.
 */
export function verificationLinkQuery(link: EmailVerificationLink): string {
  return new URLSearchParams({
    id: link.id,
    hash: link.hash,
    expires: link.expires,
    signature: link.signature,
  }).toString();
}

/**
 * The app's own address for its verify screen, with the link's four parts:
 * where the website hands a link it can't finish to the app (D12). `doapp` is
 * the app's scheme, as `mobile/app.json` declares it.
 */
export function appVerifyEmailLink(link: EmailVerificationLink): string {
  return `doapp://verify-email?${verificationLinkQuery(link)}`;
}

/**
 * Why verifying, or asking for a new link, was refused:
 * - `signed-out`: sign in, then send the same link again;
 * - `unusable`: the link was altered, has lapsed, was called at another host,
 *   is another account's, or predates an email change, so offer a new one;
 * - `rate-limited`: six a minute.
 */
export type EmailVerificationRefusal =
  'signed-out' | 'unusable' | 'rate-limited';

/**
 * Read a refusal from `verifyEmail` (the auth service's error) or
 * `sendVerificationLink` (a typed API error): both keep the API's code.
 * Undefined for anything else, such as a network failure, which the caller
 * words as a plain failure.
 */
export function emailVerificationRefusal(
  error: unknown
): EmailVerificationRefusal | undefined {
  const code = (error as { code?: unknown } | null)?.code;

  switch (code) {
    case API_ERROR_CODES.UNAUTHENTICATED:
      return 'signed-out';
    case API_ERROR_CODES.FORBIDDEN:
      return 'unusable';
    case API_ERROR_CODES.RATE_LIMITED:
      return 'rate-limited';
    default:
      return undefined;
  }
}

/**
 * Why asking for a new link failed: over six a minute (`rate-limited`), or it
 * never reached the API or the API failed (`unreachable`). Either way nothing
 * was sent, so the button that asked stays.
 */
export type SendLinkFailure = 'rate-limited' | 'unreachable';

/** Read why `sendVerificationLink` failed. */
export function sendLinkFailure(error: unknown): SendLinkFailure {
  return emailVerificationRefusal(error) === 'rate-limited'
    ? 'rate-limited'
    : 'unreachable';
}

/**
 * What to say over the button when asking for a new link failed. Over the
 * limit, it's the pause "Confirm it's you" says.
 */
export function sendLinkFailureNotice(
  failure: SendLinkFailure
): SocialSignInNotice {
  return failure === 'rate-limited'
    ? socialSignInNotice('rate_limited')
    : { variant: 'error', ...COULD_NOT_REACH_DO };
}

/** What `/verify-email` shows, on the website and in the apps. */
export type VerifyEmailView =
  /** Asking the API, as the page opens or once more. */
  | { kind: 'checking' }
  /** Verified, or it already was. */
  | { kind: 'verified' }
  /** Nobody signed in: log in, then the same link again. */
  | { kind: 'signed-out' }
  /** Refused (a 403), or `incomplete`: not a whole link, never sent. */
  | { kind: 'unusable'; incomplete: boolean }
  /** Six a minute. The link still works. */
  | { kind: 'rate-limited' }
  /** The API couldn't be reached. The link still works. */
  | { kind: 'unreachable' }
  /** A new link went to the account's address. */
  | { kind: 'sent' }
  /** A new link was asked for, but the account was verified already. */
  | { kind: 'already-verified' };

/** What `/verify-email` shows once the verify call was refused. */
export function verifyEmailRefusalView(error: unknown): VerifyEmailView {
  switch (emailVerificationRefusal(error)) {
    case 'signed-out':
      return { kind: 'signed-out' };
    case 'unusable':
      return { kind: 'unusable', incomplete: false };
    case 'rate-limited':
      return { kind: 'rate-limited' };
    default:
      return { kind: 'unreachable' };
  }
}

/** The gate's Check again: running, or how it came out. */
export type VerificationCheck = 'checking' | 'unverified' | 'unreachable';

/**
 * How Check again came out once it tried to read the account: nothing to say
 * when it's verified, since the gate then lifts, or why the gate stays.
 */
export function verificationCheckResult(
  read: boolean,
  emailVerified: boolean | undefined
): VerificationCheck | null {
  if (!read) return 'unreachable';
  return emailVerified === false ? 'unverified' : null;
}

/** What the gate says of the last Check again, over its button. */
export function verificationCheckNotice(
  check: VerificationCheck | null
): SocialSignInNotice | null {
  if (check === 'unverified') {
    return {
      variant: 'info',
      title: 'Not verified yet',
      body: 'Open the link in your email, then check again.',
    };
  }
  if (check === 'unreachable') {
    return { variant: 'error', ...COULD_NOT_REACH_DO };
  }
  return null;
}
